import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import {
  CharterActRefDto,
  CharterChangeDto,
  CorporateCharterActSummaryDto,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { VaultDatePipe } from '@shared/pipes/vault-date.pipe';
import { VaultDateTimePipe } from '@shared/pipes/vault-date-time.pipe';
import { formatarCustoUsd } from '@features/dashboard/meetings/pages/meeting-detail/meeting-insights-state';
import { CharterProvenanceComponent } from '@features/dashboard/company/corporate-charter/components/charter-provenance/charter-provenance.component';
import {
  ACT_KIND_LABEL,
  ACT_ROLE_LABEL,
  ACT_STATUS_LABEL,
  ACT_STATUS_TONE,
  CHANGE_KIND_ICON,
  MEETING_TYPE_LABEL,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';
import { CharterDocumentViewerService } from '@features/dashboard/company/corporate-charter/services/charter-document-viewer.service';

/** Rota da lista de sessões do back office, que abre a sessão pelo `?sessionId=` (a mesma do Data Room e do Meetings). */
const AGENT_SESSIONS_ROUTE = '/dashboard/management/back-office/agent-sessions';

/** Status que não fazem parte do vigente e merecem selo próprio na linha. */
const STATUS_WITH_BADGE = new Set(['processing', 'needs-review', 'failed']);

/**
 * Um ato da linha do tempo: rótulo, tipo, data, papel no vigente, o que mudou (cada mudança com a procedência que
 * abre o documento na página), pendências da conferência e os links para o documento. Para o time interno
 * (`isInternal`, TENANT ou ADMIN) mostra modelo, passadas e custo da leitura; **Sessão do agente** (só ADMIN, que é
 * quem entra no back office) abre a sessão numa aba nova, como no Data Room e no Meetings. Usuário de empresa nunca
 * vê custo de IA (o backend já manda nulo).
 */
@Component({
  selector: 'app-charter-act-item',
  imports: [VaultDatePipe, VaultDateTimePipe, CharterProvenanceComponent],
  templateUrl: './charter-act-item.component.html',
  styleUrl: './charter-act-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterActItemComponent {
  private readonly router = inject(Router);
  private readonly viewer = inject(CharterDocumentViewerService, { optional: true });

  readonly act = input.required<CharterActRefDto>();
  /** Resumo do ato (`GET acts`): status, pendências, modelo, sessão. Pode faltar em ato só da linha do tempo. */
  readonly summary = input<CorporateCharterActSummaryDto | null>(null);
  readonly changes = input<CharterChangeDto[]>([]);
  readonly actDate = input<string | null>(null);
  readonly isCurrent = input(false);
  readonly isInternal = input(false);
  readonly isAdmin = input(false);
  /** Documento sendo localizado no Data Room ("Abrindo..."). */
  readonly openingDocumentId = input<string | null>(null);

  readonly openInDataRoom = output<string>();

  protected readonly roleLabel = ACT_ROLE_LABEL;
  protected readonly statusLabel = ACT_STATUS_LABEL;
  protected readonly statusTone = ACT_STATUS_TONE;

  protected readonly kind = computed(() => {
    const meeting = this.summary()?.meetingType;
    return meeting ? MEETING_TYPE_LABEL[meeting] : ACT_KIND_LABEL[this.act().kind];
  });

  protected readonly showStatus = computed(() => STATUS_WITH_BADGE.has(this.act().status));

  protected readonly faded = computed(
    () => this.act().role === 'superseded' || this.act().role === 'not-applicable'
  );

  /** Mudanças com a procedência completa (a mudança não traz o rótulo do ato; vem do próprio ato). */
  protected readonly changeRows = computed(() => {
    const act = this.act();
    return this.changes().map(change => ({
      change,
      icon: CHANGE_KIND_ICON[change.kind] ?? 'pi-circle-fill',
      source: {
        actId: act.actId,
        actLabel: act.label,
        documentId: change.documentId ?? act.documentId,
        documentName: change.documentName ?? act.documentName,
        actDate: change.actDate ?? this.actDate(),
        page: change.page,
        quote: change.quote,
      },
    }));
  });

  /** Sem mudanças estruturadas (resposta antiga ou ato em leitura), o resumo em texto do ato. */
  protected readonly changesSummary = computed(() =>
    this.changes().length > 0 ? [] : (this.summary()?.changesSummary ?? [])
  );

  /** "Gemini 2.5 Pro · 2 passadas · US$ 0,0123": só time interno e só o que o backend mandou. */
  protected readonly agentInfo = computed(() => {
    const summary = this.summary();
    if (!this.isInternal() || !summary) {
      return null;
    }
    const parts = [
      summary.model,
      summary.passes != null
        ? `${summary.passes} ${summary.passes === 1 ? 'passada' : 'passadas'}`
        : null,
      summary.costUsd != null ? formatarCustoUsd(summary.costUsd) : null,
    ].filter((part): part is string => !!part);
    const hasSession = !!summary.agentSessionId;
    if (parts.length === 0 && !summary.processedAt && !hasSession) {
      return null;
    }
    return {
      text: parts.join(' · '),
      processedAt: summary.processedAt,
      sessionId: summary.agentSessionId,
      jobId: summary.jobId,
    };
  });

  /** Abre o documento do ato no visualizador (primeira página). */
  protected openDocument(): void {
    const act = this.act();
    this.viewer?.open({
      actId: act.actId,
      actLabel: act.label,
      documentId: act.documentId,
      documentName: act.documentName,
      actDate: this.actDate(),
      page: null,
      quote: null,
    });
  }

  /** Abre a sessão do agente no back office numa aba nova, para não perder o Societário aberto. */
  protected openSession(sessionId: string): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([AGENT_SESSIONS_ROUTE], { queryParams: { sessionId } })
    );
    window.open(url, '_blank', 'noopener');
  }
}
